import { Component, Injector, OnInit } from '@angular/core';

import {
  Address,
  AvailableCountries,
  AvailableCountry,
  EntityParsed,
  Identity,
  StepAddressValue,
  StepData
} from 'models';

import { FormControl, FormGroup } from '@angular/forms';
import { fadeAnimation } from 'animations';
import { getFormValueWithDisabledFields, inferStepValueGroup, removeNullableProperties } from 'helpers';
import isEmpty from 'lodash/isEmpty';
import { combineLatest } from 'rxjs';
import { debounceTime, filter, first } from 'rxjs/operators';
import { SimTypeEnum } from '../../../../../../app-shared/models/steps/step-sim.interface';
import { ProcessStepBaseComponent } from '../../_core/process-step-base.component';

const DEFAULT_COUNTRY_ID = 'IT';

@Component({
  selector: 'sb-step-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
  animations: [fadeAnimation]
})

export class AddressComponent extends ProcessStepBaseComponent implements OnInit {
  public inferredAddressForm: FormGroup;
  public availableCountries: AvailableCountry[] = [];

  simType: SimTypeEnum;
  SimTypes = SimTypeEnum;

  constructor(injector: Injector) {
    super(injector);
  }

  get shouldShowDeliveryAddress(): boolean {
    return this.simType !== SimTypeEnum.Standard || this.inferredAddressForm.get('residenceAsDelivery').value === false;
  }

  private static generateInferredForm(): FormGroup {
    return new FormGroup({
      residenceAsDelivery: new FormControl()
    });
  }

  ngOnInit(): void {
    // super.shouldShowBackButton();
    super.isStickyNavigation();
    this.inferredAddressForm = AddressComponent.generateInferredForm();
    super.setFormGroup(this.formsService.generateStepAddressForm());
    super.setFormStoreDataFunction(this.appStoreFacadeService.step.storeStepAddressValue);
    this.getFormDataAndValidation();
    this.capitalizeCountries();
    // Handle address
    this.handleDifferentDeliveryAddress();
    // Handle country
    this.handleCountry();
  }

  public isSameAddress(firstAddress: Address, secondAddress: Address): any {
    const firstAddressKeys = Object.keys(firstAddress);
    const secondAddressKeys = Object.keys(secondAddress);
    // Checks the same keys, ignoring the country
    for (const key of firstAddressKeys) {
      if (secondAddressKeys.includes(key)) {
        if (firstAddress[key] !== secondAddress[key]) {
          return false;
        }
      }
    }
    return true;
  }

  private capitalizeCountries(): void {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.availableCountries$,
        this.appStoreFacadeService.entity.simType$
      ])
        .pipe(filter(args => args.every(Boolean)))
        .subscribe(([ availableCountries, simType ]: [ AvailableCountries, EntityParsed ]) => {
          const capitalizeNames = (names: string) => names
            .split(' ')
            .map(n => n.charAt(0).toUpperCase() + n.substr(1).toLowerCase()).join(' ');
          this.availableCountries = availableCountries.countries.map(c => {
            return {
              id: c.id,
              name: capitalizeNames(c.name)
            };
          });
          if (simType.value && simType.value.type === SimTypeEnum.eSim) {
            this.availableCountries = this.availableCountries.filter(r => r.id === DEFAULT_COUNTRY_ID);
          }
        })
    );
  }

  private getFormDataAndValidation(): void {
    // Inner helpers
    // Help to disabled recipient.
    const addRecipientConstraint = (stepData, identity) => {
      let newStepData = stepData;
      // ARES-241: Forse recipient disabled.
      if (identity) {
        newStepData = {
          ...stepData,
          customerDeliveryAddress: {
            ...stepData.customerDeliveryAddress,
            options: {
              ...stepData.customerDeliveryAddress.options,
              recipient: {
                disabled: true
              }
            }
          }
        };
      }
      return newStepData;
    };

    const patchInferredValue = (stepData: StepAddressValue, residenceAddress: Address) => {
      const isSameCountry = !residenceAddress.country || residenceAddress.country === DEFAULT_COUNTRY_ID;
      let inferredValue = false;
      if (isSameCountry) {
        inferredValue = this.simType === SimTypeEnum.Standard && this.isSameAddress(residenceAddress, stepData.customerDeliveryAddress);
      }
      this.inferredAddressForm.patchValue({ residenceAsDelivery: inferredValue });
      return inferredValue;
    };

    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.identity$,
        this.appStoreFacadeService.entity.residenceAddress$,
        this.appStoreFacadeService.entity.simType$,
        this.appStoreFacadeService.step.address$
      ])
      .pipe(filter((args) => args.every(Boolean)))
      .subscribe(([identity, residenceAddress, simType, stepData]: [EntityParsed, EntityParsed, EntityParsed, StepData]) => {
        // ARES-241: Forse recipient disabled.
        const newStepData = addRecipientConstraint(stepData, identity);
        super.setFormGroupStepData(newStepData);
        if (identity) {
          this.setRecipientValue(identity.value as Identity);
        }
        this.simType = (simType.value && simType.value.type) as unknown as SimTypeEnum;
        // Patch checkbox
        const residenceAsDelivery = patchInferredValue(
          inferStepValueGroup(stepData) as StepAddressValue, residenceAddress.value as Address);
        this.onUseResidenceAsDeliveryChange(residenceAsDelivery);
        if (this.simType !== SimTypeEnum.Standard) {
          this.formGroup.get('customerDeliveryAddress').disable();
        }
      })
    );
    this.subscriptions.add(
      this.appStoreFacadeService.step.addressValue$
        .pipe(debounceTime(150), first(Boolean))
        .subscribe(() => {
          this.sendAnalyticsData();
        })
    );
  }

  private setRecipientValue(identity: Identity): void {
    const { firstName, lastName } = identity;
    const recipientField = this.formGroup.get('customerDeliveryAddress.recipient');
    recipientField.patchValue(`${firstName} ${lastName}`);
    // recipientField.disable();
  }

  public onCheckAddressClick() {
    setTimeout(() => {
      this.analyticsService.sendCheckSameAddressAnalytics(this.inferredAddressForm.get('residenceAsDelivery').value);
    }, 0);
  }

  goToNextStep() {
    const residenceAsDelivery = this.inferredAddressForm.get('residenceAsDelivery').value;
    const deliveryAddressFormValue = this.formGroup.get('customerDeliveryAddress').value;
    const residenceAddressFormValue = this.formGroup.get('customerResidenceAddress').value;

    if (this.formGroup.invalid) {
      this.markFormAsDirtyAndScrollToError();
      return;
    }

    const inferResidenceAddress = () => {
      const selectedCountry = residenceAddressFormValue.country;
      let residenceAddressData;

      if (this.simType !== SimTypeEnum.Standard || !residenceAsDelivery) {
        if (selectedCountry !== null && selectedCountry !== DEFAULT_COUNTRY_ID) {
          residenceAddressData = {
            ...deliveryAddressFormValue,
            country: residenceAddressFormValue.country
          };
        } else {
          residenceAddressData = residenceAddressFormValue;
        }
      } else {
        residenceAddressData = {
          ...deliveryAddressFormValue,
          country: DEFAULT_COUNTRY_ID
        };

        // cleans the full address

        const residenceAddressControls = (this.formGroup.get('customerResidenceAddress') as FormGroup).controls;
        const deliveryAddressKeys = Object.keys(deliveryAddressFormValue);
        deliveryAddressKeys.forEach((key) => {
          if (!(key in residenceAddressControls)) {
            delete residenceAddressData[key];
          }
        });

      }
      return residenceAddressData;
    };

    const formData: StepAddressValue = {
      ...getFormValueWithDisabledFields(this.formGroup, this.alwaysDisabledFormControlsKeys || []),
      ...this.formGroup.value,
      customerResidenceAddress: inferResidenceAddress()
    };

    /* if (
      formData.customerDeliveryAddress &&
      formData.customerDeliveryAddress.zipCode &&
      FORBIDDEN_ZIP_CODES.includes(`${formData.customerDeliveryAddress.zipCode}`)) {
      this.appStoreFacadeService.errors.openErrorModal('ENTITY.ADDRESS.COVID');
      return;
    } */


    this.formGroup.markAsPristine();
    this.storeFormDataFunction(formData);
    this.appStoreFacadeService.navigation.goNext();
  }

  private onUseResidenceAsDeliveryChange = useResidenceAsDelivery => {
    const deliveryAddressForm = this.formGroup.get('customerDeliveryAddress');
    const residenceAddressForm = this.formGroup.get('customerResidenceAddress');
    // If addresses are equal... reset the delivery address
    if (this.simType === SimTypeEnum.Standard && useResidenceAsDelivery) {
      residenceAddressForm.reset();
      residenceAddressForm.disable();
    } else {
      residenceAddressForm.enable();
      // Enable address and reset its fields in case of foreign country
      const isEmptyAddress = isEmpty(removeNullableProperties(residenceAddressForm.value));
      const selectedCountry = residenceAddressForm.get('country').value;
      if (residenceAddressForm.get('country').value === DEFAULT_COUNTRY_ID) {
        if (this.isSameAddress(residenceAddressForm.value, deliveryAddressForm.value) || isEmptyAddress) {
          residenceAddressForm.reset();
          residenceAddressForm.patchValue({
            country: DEFAULT_COUNTRY_ID
          });
        }
      } else {
        residenceAddressForm.reset();
        residenceAddressForm.patchValue({
          country: selectedCountry || DEFAULT_COUNTRY_ID
        });
      }
    }
    this.onCountryChange();
  }

  private handleDifferentDeliveryAddress(): void {
    const residenceAddressCheck = this.inferredAddressForm.get('residenceAsDelivery');
    this.subscriptions.add(
      residenceAddressCheck.valueChanges.subscribe((value) => this.onUseResidenceAsDeliveryChange(value))
    );
  }

  private onCountryChange(): void {
    const residenceAddressForm = this.formGroup.get('customerResidenceAddress') as FormGroup;
    if (residenceAddressForm.enabled) {
      const countryValue = residenceAddressForm.get('country').value;
      const controlsToDisable = Object.keys(residenceAddressForm.controls).filter((controlKey) => controlKey !== 'country');
      controlsToDisable.forEach((formControlKey) => {
        const formControl = residenceAddressForm.get(formControlKey);
        if (countryValue !== null && countryValue !== DEFAULT_COUNTRY_ID) {
          formControl.reset();
          formControl.disable();
        } else {
          formControl.enable();
        }
      });
    }
  }

  private handleCountry(): void {
    const residenceAddressFormGroup = this.formGroup.get('customerResidenceAddress') as FormGroup;
    const countryControl = residenceAddressFormGroup.get('country');
    this.subscriptions.add(
      countryControl.valueChanges.pipe(
        filter(Boolean)
      ).subscribe(() => this.onCountryChange())
    );
  }
}
